import { h } from "snabbdom";
import type { VNode } from "snabbdom";
import type { CardGroup, CardSummary, Msg, TagValue, ViewConfig } from "../../types";
import { previewHost } from "../../editor/preview";
import { PREVIEW_LINES } from "../../preview-limits";

const NONE_COLUMN = "(none)";

// A bare field name (vs. an arbitrary CEL expression). Only then can a drop be
// reversed into a tag write.
const FIELD_RE = /^[A-Za-z_]\w*$/;

// The card currently being dragged. Held module-side so the drop target (a
// column, which only knows the card id) can merge against the card's full tag
// set rather than clobbering every other tag.
let draggedCard: CardSummary | null = null;

// The column element currently highlighted as the drop target. Toggled
// imperatively (the kanban renders in an isolated node-view tree, and updating
// this through the Model would re-render on every dragover).
let dropCol: HTMLElement | null = null;
function setDropTarget(col: HTMLElement | null): void {
  if (dropCol === col) return;
  dropCol?.classList.remove("kanban-col--drop");
  col?.classList.add("kanban-col--drop");
  dropCol = col;
}

export function viewKanbanView(
  config: ViewConfig,
  groups: CardGroup[],
  dispatch: (msg: Msg) => void,
  depth: number
): VNode {
  // Grouping/ordering/filtering are all server-side. We only arrange the
  // returned groups into columns. Drag-drop is enabled only when groupBy is a
  // plain field, since an arbitrary expression can't be reversed into a write.
  const field = config.groupBy && FIELD_RE.test(config.groupBy.trim()) ? config.groupBy.trim() : null;
  const columns = orderedColumns(groups, config);

  return h(
    "div.view-kanban",
    columns.map(([colValue, colCards]) => viewColumn(colValue, colCards, field, dispatch, depth))
  );
}

// Column order: configured columns first (always shown, even when empty), then —
// if showAllColumns is on (default) — any remaining groups in result order.
function orderedColumns(groups: CardGroup[], config: ViewConfig): [string, CardSummary[]][] {
  const byValue = new Map<string, CardSummary[]>();
  for (const group of groups) {
    byValue.set(group.group ?? NONE_COLUMN, group.cards);
  }

  const configured = config.columns ?? [];
  const showAll = config.showAllColumns !== false;
  const result: [string, CardSummary[]][] = [];
  const seen = new Set<string>();

  for (const col of configured) {
    result.push([col, byValue.get(col) ?? []]);
    seen.add(col);
  }
  if (showAll) {
    for (const [col, colCards] of byValue) {
      if (!seen.has(col)) {
        result.push([col, colCards]);
        seen.add(col);
      }
    }
  }
  return result;
}

function viewColumn(
  colValue: string,
  cards: CardSummary[],
  field: string | null,
  dispatch: (msg: Msg) => void,
  depth: number
): VNode {
  return h("div.kanban-col", {
    attrs: { "data-column": colValue },
    on: field
      ? {
          dragover: (e: DragEvent) => {
            e.preventDefault();
            setDropTarget(e.currentTarget as HTMLElement);
          },
          drop: (e: DragEvent) => {
            e.preventDefault();
            setDropTarget(null);
            const card = draggedCard;
            draggedCard = null;
            if (!card) return;
            const current = card.tags[field];
            const currentValue = current && current.type === "string" ? current.value : NONE_COLUMN;
            // No-op drop onto the same column — skip the write.
            if (currentValue === colValue) return;
            // (none) means "clear the column tag"; otherwise set it.
            const newTags = { ...card.tags };
            if (colValue === NONE_COLUMN) {
              delete newTags[field];
            } else {
              const newTagValue: TagValue = { type: "string", value: colValue };
              newTags[field] = newTagValue;
            }
            dispatch({ type: "SaveCard", id: card.id, patch: { tags: newTags } });
          },
        }
      : {},
  }, [
    h("h3.kanban-col__title", colValue),
    h(
      "div.kanban-col__cards",
      cards.map((card) => viewKanbanCard(card, !!field, dispatch, depth))
    ),
  ]);
}

function viewKanbanCard(
  card: CardSummary,
  draggable: boolean,
  dispatch: (msg: Msg) => void,
  depth: number
): VNode {
  return h(
    "div.kanban-card",
    {
      attrs: { draggable: String(draggable) },
      on: {
        dragstart: (e: DragEvent) => {
          draggedCard = card;
          e.dataTransfer?.setData("text/plain", card.id);
        },
        dragend: () => {
          draggedCard = null;
          setDropTarget(null);
        },
        click: () => dispatch({ type: "NavigateTo", id: card.id }),
      },
    },
    [previewHost(card.content, depth, dispatch, PREVIEW_LINES.kanban)]
  );
}
